import { config } from 'config'
import Keycloak, {
  KeycloakConfig,
  KeycloakInitOptions,
  KeycloakLoginOptions,
} from 'keycloak-js'

import { LOGOUT } from 'routes/paths'

/**
 * KeycloakConfig configures the connection to the Keycloak server.
 */
const keycloakConfig: KeycloakConfig = {
  realm: 'joor',
  clientId: 'joor-react',
  url: config.urls.keycloak,
}

/**
 * Preferred init: resolves the session through a hidden iframe, so there is no
 * visible redirect on cold start.
 *
 * WHY the timeout is short: keycloak-js will not use the silent iframe (nor the
 * login-status iframe) until it has loaded `.../3p-cookies/step1.html` in an iframe
 * and received a postMessage back. A healthy probe answers in 100-300ms. If it
 * answers nothing at all we wait `messageReceiveTimeout` and then init() rejects,
 * which is dead time on a blank screen. A browser that is merely slow falls through
 * to the redirect path, which works fine, so a short timeout costs us nothing.
 */
const authInitOptions: KeycloakInitOptions = {
  onLoad: 'check-sso',
  silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
  checkLoginIframe: true,
  checkLoginIframeInterval: 60, // 1 minute
  messageReceiveTimeout: 3000, // 3 seconds
}

/**
 * Fallback init, used when the preferred one fails. Resolves the session with a
 * top-level redirect carrying `prompt=none` instead of an iframe, so Keycloak reads
 * its own cookie in a first-party context. No form is ever shown: `prompt=none`
 * forbids it. The user sees a brief redirect flash and nothing else.
 *
 * WHY both options are gone and not just `checkLoginIframe: false`: keycloak-js
 * runs the probe when `checkLoginIframe || silentCheckSsoRedirectUri` — it is an OR.
 * Leaving `silentCheckSsoRedirectUri` set would keep the probe, and the failure,
 * exactly where they are. With neither, no iframe is ever created.
 *
 * Cost: one visible redirect on cold start, and a Keycloak-side logout is picked up
 * on the next token refresh instead of within `checkLoginIframeInterval`.
 */
const authInitFallbackOptions: KeycloakInitOptions = {
  onLoad: 'check-sso',
  checkLoginIframe: false,
}

/**
 * WHY we remember the failure: the probe fails deterministically per browser, and a
 * cold start happens on every hard navigation — including moves between joor-react,
 * joor-angular and assortments-ui. Without this, affected users pay the timeout
 * every single time. With it, once per browser.
 *
 * The flag does not expire, so a browser that later gains support stays on the
 * redirect path. That path works correctly; it only loses the flash-free start.
 */
const SILENT_SSO_BROKEN_KEY = 'auth:silentSsoBroken'

const isSilentSsoKnownBroken = (): boolean => {
  try {
    return window.localStorage.getItem(SILENT_SSO_BROKEN_KEY) === 'true'
  } catch {
    return false // private mode or storage disabled
  }
}

const rememberSilentSsoBroken = () => {
  try {
    window.localStorage.setItem(SILENT_SSO_BROKEN_KEY, 'true')
  } catch {
    // best effort: worst case we pay the timeout again on the next cold start
  }
}

const createInstance = () => {
  const instance = new Keycloak(keycloakConfig)

  instance.onTokenExpired = () => {
    instance.updateToken(30).catch((_error) => {
      instance.clearToken()
      window.location.assign(LOGOUT)
      instance.login()
    })
  }

  return instance
}

// Reassigned when we fall back, so every accessor below has to read it at call
// time instead of capturing a method reference once at module load.
let authInstance = createInstance()

/**
 * Resolves to `true` when the user has a live session.
 *
 * WHY a fresh instance for the fallback: keycloak-js guards against initializing
 * the same instance twice, and a half-initialized instance is not a state worth
 * reasoning about. A new one is cheap.
 *
 * WHY the same options are never retried: the failure this recovers from is
 * deterministic, not transient. Retrying identical options cannot succeed — it only
 * adds another `messageReceiveTimeout` of blank screen.
 *
 * If the fallback throws too, this rejects. Callers must land on an error screen or
 * a login redirect: never on an empty page.
 */
const init = async (): Promise<boolean> => {
  if (!isSilentSsoKnownBroken()) {
    try {
      return await authInstance.init(authInitOptions)
    } catch (error) {
      // Distinctive string so the fallback rate stays queryable in RUM.
      console.warn(
        '[auth] silent SSO init failed, falling back to redirect',
        error,
      )
      rememberSilentSsoBroken()
      authInstance = createInstance()
    }
  }

  return await authInstance.init(authInitFallbackOptions)
}

export const auth = {
  init,
  login: (options?: KeycloakLoginOptions) => authInstance.login(options),
  // WHY a real Keycloak logout (not just clearing the local token): a No Access
  // user is still valid as far as Keycloak's own session/cache is concerned —
  // only ending the Keycloak session forces re-authentication instead of a
  // silent SSO re-login straight back into the same account.
  logout: (redirectUri?: string) => authInstance.logout({ redirectUri }),
  getToken: () => authInstance.token,
}
